import { linter } from '@codemirror/lint';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { filterFieldMessages } from '../messages.js';
import type { FilterFieldCustomTypes } from '../types/validation.js';
import {
  getCustomTypeLabel,
  getTypeLabelKey,
} from '../utils/error-utils/error-message-helpers.js';
import { generateErrorsOrDiagnostics } from '../utils/generate-errors-or-diagnostics.js';

/** @internal */
export function useFilterFieldLinting(customTypes?: FilterFieldCustomTypes) {
  const intl = useIntl();

  return useMemo(
    () =>
      linter((view) =>
        generateErrorsOrDiagnostics(view, {
          emptyGroupError: intl.formatMessage(
            filterFieldMessages.linting.emptyGroupError,
          ),
          incompleteGroupError: intl.formatMessage(
            filterFieldMessages.linting.incompleteGroupError,
          ),
          unsupportedExplicitGroup: intl.formatMessage(
            filterFieldMessages.linting.unsupportedExplicitGroup,
          ),
          unsupportedPositionOperator: intl.formatMessage(
            filterFieldMessages.linting.unsupportedPositionOperator,
          ),
          invalidWildCardOperatorInList: intl.formatMessage(
            filterFieldMessages.linting.invalidWildCardOperatorInList,
          ),
          duplicatePositionOperator: intl.formatMessage(
            filterFieldMessages.linting.duplicatePositionOperator,
          ),
          invalidPositionOperator: intl.formatMessage(
            filterFieldMessages.linting.invalidPositionOperator,
          ),
          incompleteStatementError: intl.formatMessage(
            filterFieldMessages.linting.incompleteStatementError,
          ),
          missingKeyError: intl.formatMessage(
            filterFieldMessages.linting.missingKeyError,
          ),
          missingValueError: intl.formatMessage(
            filterFieldMessages.linting.missingValueError,
          ),
          missingOperatorError: intl.formatMessage(
            filterFieldMessages.linting.missingOperatorError,
          ),
          invalidValueErrorGeneric: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorGeneric,
          ),
          invalidValueErrorDurations: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorDurations,
          ),
          invalidValueErrorNumbers: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorNumbers,
          ),
          invalidValueErrorStrings: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorStrings,
          ),
          invalidValueErrorIPAddresses: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorIPAddresses,
          ),
          invalidValueErrorUIDs: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorUIDs,
          ),
          invalidValueErrorTimestamps: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorTimestamps,
          ),
          invalidValueErrorSmartscapeIds: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorSmartscapeIds,
          ),
          invalidValueErrorBoolean: intl.formatMessage(
            filterFieldMessages.linting.invalidValueErrorBoolean,
          ),
          invalidLogicalOperator: intl.formatMessage(
            filterFieldMessages.linting.invalidLogicalOperator,
          ),
          unsupportedLogicalOperator: intl.formatMessage(
            filterFieldMessages.linting.unsupportedLogicalOperator,
          ),
          invalidInclusionValue: intl.formatMessage(
            filterFieldMessages.linting.invalidInclusionValue,
          ),
          emptyInclusionValue: intl.formatMessage(
            filterFieldMessages.linting.emptyInclusionValue,
          ),
          missingSpace: intl.formatMessage(
            filterFieldMessages.linting.missingSpace,
          ),
          invalidKey: intl.formatMessage(
            filterFieldMessages.linting.invalidKey,
          ),
          invalidWildCardOperator: intl.formatMessage(
            filterFieldMessages.linting.invalidWildCardOperator,
          ),
          invalidCharacterInIdentifier: intl.formatMessage(
            filterFieldMessages.linting.invalidCharacterInIdentifier,
          ),
          unsupportedSearchOperator: intl.formatMessage(
            filterFieldMessages.linting.unsupportedSearchOperator,
          ),
          invalidSearchOperatorInGroup: intl.formatMessage(
            filterFieldMessages.linting.invalidSearchOperatorInGroup,
          ),
          unsupportedSearchWithLogicalOr: (allowedOperator = 'AND') =>
            intl.formatMessage(
              filterFieldMessages.linting.unsupportedSearchWithLogicalOr,
              {
                allowedOperator,
              },
            ),
          invalidComparatorError: (
            allowedComparators = '=, !=, >, >=, <, <=',
          ) =>
            intl.formatMessage(
              filterFieldMessages.linting.invalidComparatorError,
              {
                allowedComparators,
              },
            ),
          invalidValueErrorMultipleTypes: (serializedTypes: string[] = []) => {
            const typeLabels = serializedTypes.map((type) => {
              const labelKey = getTypeLabelKey(type);
              if (labelKey) {
                return intl.formatMessage(
                  filterFieldMessages.linting.typeLabels[labelKey],
                );
              }
              return getCustomTypeLabel(type, customTypes) ?? type;
            });
            const allowedTypes = new Intl.ListFormat(intl.locale, {
              style: 'long',
              type: 'conjunction',
            }).format(typeLabels);
            return intl.formatMessage(
              filterFieldMessages.linting.invalidValueErrorMultipleTypes,
              { allowedTypes },
            );
          },
          incompleteStatementWithEnabledSearchError: (operator = '* ~') =>
            intl.formatMessage(
              filterFieldMessages.linting
                .incompleteStatementWithEnabledSearchError,
              {
                operator,
              },
            ),
        }),
      ),
    [intl, customTypes],
  );
}
